/**
 * Select — shadcn Select 어댑터
 *
 * 기존 @/ui Select API(options 배열 + label/error + value/onChange)를 유지하되
 * 내부는 shadcn(Radix) Select로 위임한다. 네이티브 select의 onChange 이벤트
 * 대신 onValueChange를 흡수해 기존 소비처의 value/defaultValue/onChange를 지원.
 *
 * NOTE: forwardRef는 복원하지 않는다. shadcn Select는 Radix 기반으로 네이티브
 * <select> 엘리먼트가 없어 HTMLSelectElement ref를 전달할 수 없다. 기존 소비처도
 * ref를 전달하는 곳이 없으므로 문제 없다.
 */
import { SelectHTMLAttributes, ChangeEvent } from 'react'
import {
  Select as ShadSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { cn } from './cn'

interface SelectOption {
  value: string
  label: string
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg'
  label?: string
  error?: string
  options: SelectOption[]
}

const TRIGGER_SIZE = {
  sm: 'h-8 text-sm',
  md: 'h-9 text-sm',
  lg: 'h-10 text-base',
} as const

/**
 * Radix SelectItem은 value=""를 허용하지 않아 런타임 에러를 던진다.
 * 소비처(25개 페이지)는 '전체' 옵션으로 value=''를 광범위하게 사용하므로
 * 내부에서 빈 문자열을 센티넬 값으로 치환해 소비처 코드 변경 없이 투명하게 처리한다.
 */
const EMPTY_SENTINEL = '__empty__'
const toRadix = (v: string) => (v === '' ? EMPTY_SENTINEL : v)
const fromRadix = (v: string) => (v === EMPTY_SENTINEL ? '' : v)

export function Select({
  size = 'md',
  label,
  error,
  options,
  className,
  id,
  value,
  defaultValue,
  onChange,
  disabled,
  name,
}: SelectProps) {
  // 기존 소비처가 e.target.value 기반 onChange를 기대하므로 합성 이벤트로 변환
  // fromRadix: Radix에서 받은 센티넬 값을 소비처가 기대하는 빈 문자열로 복원
  const handleValueChange = (next: string) => {
    onChange?.({ target: { value: fromRadix(next), name } } as ChangeEvent<HTMLSelectElement>)
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground" htmlFor={id}>
          {label}
        </label>
      )}
      <ShadSelect
        value={value !== undefined ? toRadix(value as string) : undefined}
        defaultValue={defaultValue !== undefined ? toRadix(defaultValue as string) : undefined}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          className={cn(TRIGGER_SIZE[size], error && 'border-destructive')}
        >
          {/* placeholder: value 미지정 시 빈 트리거 방지 */}
          <SelectValue placeholder="선택" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            // toRadix: '' → '__empty__' 로 치환해 Radix 크래시 방지 (key는 원본 유지)
            <SelectItem key={opt.value} value={toRadix(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </ShadSelect>
      {error && <span className="text-sm text-destructive">{error}</span>}
    </div>
  )
}
